import React from 'react';
import styles from './styles.less';
import { ChatColumn, ChatContext, Starter } from './types.ts';

interface Props {
    context: ChatContext | null;
    starters: Starter[] | null;  // null = still generating
    startersError?: boolean;
    onPick: (prompt: string) => void;
    onRetry?: () => void;
    picked?: Set<string>;
}

export function StarterChips({ starters, picked, onPick }: { starters: Starter[]; picked?: Set<string>; onPick: (prompt: string) => void; }) {
    return (
        <div className={styles.starterChips}>
            {starters.map((s) => {
                const isPicked = !!picked && picked.has(s.prompt);
                return (
                    <button
                        key={s.prompt}
                        className={isPicked ? `${styles.starterChip} ${styles.starterChipPicked}` : styles.starterChip}
                        onClick={() => onPick(s.prompt)}
                        title={s.prompt}
                    >
                        <i className={`fa ${isPicked ? 'fa-check' : 'fa-play'} ${styles.starterIcon}`} aria-hidden="true" />
                        {s.label}
                    </button>
                );
            })}
        </div>
    );
}

function greetingText(ctx: ChatContext | null): string {
    if (!ctx || !ctx.numNodes) {
        return 'What would you like to explore first? Pick one below, or ask anything about your graph.';
    }
    const cols = (ctx.nodeColumns || [])
        .map((c: string | ChatColumn) => typeof c === 'string' ? c : c.name)
        .filter((c) => !c.includes('.') && !/^(id|node_id|label)$/i.test(c))
        .slice(0, 4);
    const colPart = cols.length ? ` Each node has details like ${cols.join(', ')}.` : '';
    const title = ctx.title ? `"${ctx.title}" — ` : '';
    const nodes = ctx.numNodes.toLocaleString();
    const edges = (ctx.numEdges || 0).toLocaleString();
    return `${title}This graph has ${nodes} nodes and ${edges} edges.${colPart} What would you like to explore first?`;
}

function StartersFallback({ message, onRetry }: { message: string; onRetry?: () => void }) {
    return (
        <div className={styles.startersFallback}>
            <span>{message}</span>
            {onRetry && (
                <button type="button" className={styles.starterRetry} onClick={onRetry}>
                    Retry
                </button>
            )}
        </div>
    );
}

export function ChatEmptyState({ context, starters, startersError, onPick, onRetry, picked }: Props) {
    return (
        <div className={styles.emptyState}>
            <div className={styles.greeting}>{greetingText(context)}</div>
            {startersError ? (
                <StartersFallback message="Couldn’t load suggestions." onRetry={onRetry} />
            ) : starters === null ? (
                <div className={styles.startersLoading}>Generating suggestions…</div>
            ) : starters.length === 0 ? (
                <StartersFallback message="No suggestions yet — ask anything about your graph." onRetry={onRetry} />
            ) : (
                <StarterChips starters={starters} picked={picked} onPick={onPick} />
            )}
        </div>
    );
}

export default ChatEmptyState;
